import {useContext, useEffect, useState} from 'react';
import {useInfiniteQuery, useQueryClient} from '@tanstack/react-query';
import {AxiosError} from 'axios';
import {ToastType} from 'patient-ping-remedy/packages/toast';
import {MessageThreadSummary} from '@/api/dto/message';
import {CarecoApiContext} from '@/app-context/careco-api-context';
import {useAlertStore} from '@/store/alert_store';
import {useMessageStore} from '@/store/message_store';
import {useRosterStore} from '@/store/roster_store';
import {GENERICERROR} from '@manual_encounters/constants/BannerMessages';
import Helpers from '@/helpers/helpers';
import {log} from '@/helpers/logger';

type Options = {
  onlyShowUnreadMessages: boolean;
  onlyUpdateCount?: boolean;
  enabled?: boolean;
  searchTerm?: string;
  threadType?: string;
}

export function useMessageThreadInbox(
  { onlyShowUnreadMessages, onlyUpdateCount, enabled = true, searchTerm, threadType }: Options
) {
  const { carecoApi } = useContext(CarecoApiContext);
  const queryClient = useQueryClient();
  const addAlert = useAlertStore((state) => state.addAlert);
  const currentRoster = useRosterStore((state) => state.currentRoster);
  const [messageThreadSummariesSearched, setMessageThreadSummariesSearched] = useState<MessageThreadSummary[] | null>(null);
  const [isMessageUpdateLoading, setMessageUpdateLoading] = useState(false);

  const {
    isApiLoading,
    setApiLoading,
    messageThreadSummaries,
    setMessageThreadSummaries,
    totalMessages,
    setTotalMessages,
    totalUnreadMessages,
    setTotalUnreadMessages,
  } = useMessageStore();

  const {
    data,
    isLoading,
    isFetching,
    isRefetching,
    isFetched,
    error,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: ['messageThreadInbox', onlyShowUnreadMessages, searchTerm, threadType, currentRoster?.id],
    queryFn: async ({ pageParam }) =>
      carecoApi?.getMessageThreadInbox(onlyShowUnreadMessages, pageParam, searchTerm, threadType, currentRoster?.id ?? undefined),
    enabled: enabled && !!carecoApi,
    getNextPageParam: (lastPage) => lastPage?.nextPage,
    initialPageParam: 1,
    refetchInterval: 180_000,
  });

  const allThreadSummaries = data?.pages.flatMap((page) => page?.data.messageThreadSummaries ?? [])
    .sort((a, b) => new Date(b.latestMessage.createdAt).getTime() - new Date(a.latestMessage.createdAt).getTime()) ?? [];

  const rosterFilteredSummaries = searchTerm ? null : allThreadSummaries;
  const rosterTotal = data?.pages[0]?.data.rosterTotal ?? 0;

  useEffect(() => {
    if (data) {
      if (!onlyUpdateCount) {
        const messageThreadSummaries = data.pages.flatMap((page) => page?.data.messageThreadSummaries ?? [])
          .sort((a, b) => new Date(b.latestMessage.createdAt).getTime() - new Date(a.latestMessage.createdAt).getTime());
        if (searchTerm) {
          setMessageThreadSummariesSearched(messageThreadSummaries);
        } else {
          setMessageThreadSummariesSearched(null);
          setMessageThreadSummaries(messageThreadSummaries);
          setTotalMessages(data.pages[0]?.data.rosterTotal ?? 0);
        }
      }
      if (onlyShowUnreadMessages) {
        setTotalUnreadMessages(data.pages[0]?.data.rosterTotal ?? 0);
      }
    }
  }, [data]);

  useEffect(() => {
    setApiLoading(isLoading || isMessageUpdateLoading);
  }, [isLoading, isMessageUpdateLoading]);

  // Mark all message threads read across all accessible rosters
  const markAllAsRead = () => {
    setMessageUpdateLoading(true);
    carecoApi?.readAllMessageThreads()
      .then(() => {
        addAlert({
          type: ToastType.SUCCESS,
          content: 'All messages marked as read.',
        });
        void queryClient.invalidateQueries({ queryKey: ['messageThreadInbox'], exact: false });
      })
      .catch((error: AxiosError) => {
        addAlert({
          type: ToastType.ERROR,
          content: `Failed to mark all messages as read. ${GENERICERROR} ${Helpers.traceId(error)}`,
        });
        log('error', 'Error updating all messages as read', error);
      })
      .finally(() => {
        setMessageUpdateLoading(false);
      });
  };

  return {
    isLoading,
    isApiLoading,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isRefetching,
    isFetched,
    error,
    messageThreadSummaries: rosterFilteredSummaries ?? messageThreadSummaries,
    messageThreadSummariesSearched,
    totalMessages: rosterTotal || totalMessages,
    totalUnreadMessages,
    markAllAsRead,
  };
}
